"use client";

import { useQueryClient } from "@tanstack/react-query";
import {
	ArrowLeft,
	Clock,
	History,
	Lightbulb,
	LogOutIcon,
	Music2,
} from "lucide-react";
import Link from "next/link";
import { useRouter } from "next/navigation";
import { toast } from "sonner";

import { CreditsDisplay } from "@/components/credits/credits-display";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Logo } from "@/components/ui/logo";
import {
	Sidebar,
	SidebarContent,
	SidebarFooter,
	SidebarHeader,
} from "@/components/ui/sidebar";
import { useUser } from "@/hooks/useUser";
import { clearLastUsedProjectCookiesAction } from "@/lib/actions/project";
import { useAuth } from "@/lib/auth-client";

import type { Organization } from "@/lib/types";

export interface MusicHistoryItem {
	id: string;
	prompt: string | null;
	created: number;
	audio: { url: string; mime_type: string } | null;
	expired?: boolean;
	error?: boolean;
	metadata?: { used_model?: string; response_format?: string };
	usage?: { cost_inr?: number | null };
}

interface MusicSidebarProps {
	className?: string;
	selectedOrganization: Organization | null;
	history?: MusicHistoryItem[];
	historyLoading?: boolean;
	onPickHistory?: (item: MusicHistoryItem) => void;
}

/**
 * Sidebar for the Music Studio — branding, prompt tips, and account/credits
 * only. Org selection lives in the top header bar (same as Video).
 */
export function MusicSidebar({
	className,
	selectedOrganization,
	history,
	historyLoading,
	onPickHistory,
}: MusicSidebarProps) {
	const queryClient = useQueryClient();
	const router = useRouter();
	const { user, isLoading: isUserLoading } = useUser();
	const { signOut } = useAuth();

	const logout = async () => {
		try {
			await clearLastUsedProjectCookiesAction();
		} catch {
			toast.error("Failed to clear session");
		}
		await signOut({
			fetchOptions: {
				onSuccess: () => {
					queryClient.clear();
					router.push(
						process.env.NODE_ENV === "development"
							? "http://localhost:3003/login"
							: "https://playground.routerbench.com/login",
					);
				},
			},
		});
	};

	const isAuthenticated = !!user;

	if (!isUserLoading && !isAuthenticated) {
		return (
			<Sidebar className={className}>
				<SidebarHeader>
					<div className="mb-4 flex flex-col items-center gap-4">
						<Link
							href="/"
							className="my-2 flex items-center gap-2 self-start"
							prefetch={true}
						>
							<Logo className="h-10 w-10" />
							<h1 className="text-xl font-semibold">RouterBench</h1>
							<Badge>Music</Badge>
						</Link>
						<div className="w-full rounded-md border p-4 text-sm">
							<div className="mb-2 font-medium">Sign in required</div>
							<p className="mb-3 text-muted-foreground">
								Please sign in to generate music.
							</p>
							<div className="flex items-center justify-end gap-2">
								<Button size="sm" asChild>
									<Link href="/login">Sign in</Link>
								</Button>
								<Button size="sm" variant="outline" asChild>
									<Link href="/signup">Create account</Link>
								</Button>
							</div>
						</div>
					</div>
				</SidebarHeader>
			</Sidebar>
		);
	}

	return (
		<Sidebar className={className + " max-md:hidden"}>
			<SidebarHeader className="px-4 pb-2 pt-4">
				<div className="flex flex-col gap-3">
					<Link
						href="/"
						className="flex items-center gap-2 px-1"
						prefetch={true}
					>
						<Logo className="h-8 w-8" />
						<h1 className="text-lg font-semibold tracking-tight">
							RouterBench
						</h1>
						<Badge variant="secondary" className="h-5 px-1.5 py-0 text-[10px]">
							Music
						</Badge>
					</Link>
					<Button
						variant="outline"
						className="h-9 w-full justify-center gap-2 rounded-xl text-sm"
						asChild
					>
						<Link href="/">
							<ArrowLeft className="h-4 w-4" />
							Back to Chat
						</Link>
					</Button>
				</div>
			</SidebarHeader>

			<SidebarContent className="px-3 pt-4 space-y-3">
				{/* History — recent generations for this org. Click to replay. */}
				<div className="rounded-xl border border-border/60 bg-background/60">
					<div className="flex items-center justify-between px-3 py-2 border-b border-border/60">
						<div className="flex items-center gap-1.5 text-xs font-medium">
							<History className="h-3.5 w-3.5 text-muted-foreground" />
							Recent tracks
						</div>
						{historyLoading && (
							<span className="text-[10px] text-muted-foreground">
								Loading…
							</span>
						)}
					</div>
					<div className="max-h-64 overflow-y-auto">
						{!history || history.length === 0 ? (
							<div className="px-3 py-4 text-[11px] text-muted-foreground">
								{historyLoading ? "Loading history…" : "No tracks yet."}
							</div>
						) : (
							<ul className="divide-y divide-border/40">
								{history.map((h) => (
									<li key={h.id}>
										<button
											type="button"
											onClick={() => onPickHistory?.(h)}
											className="flex w-full items-start gap-2 px-3 py-2 text-left transition-colors hover:bg-muted/40 disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:bg-transparent"
											disabled={h.expired || !h.audio}
											title={
												h.expired
													? "Expired (older than 30 days)"
													: h.audio
														? "Click to replay"
														: "Audio unavailable"
											}
										>
											<Music2 className="mt-0.5 h-3.5 w-3.5 shrink-0 text-muted-foreground" />
											<div className="min-w-0 flex-1">
												<div className="truncate text-[11.5px] font-medium text-foreground">
													{h.prompt?.slice(0, 80) || "(no prompt)"}
												</div>
												<div className="mt-0.5 flex items-center gap-1.5 text-[10px] text-muted-foreground">
													<span>
														{new Date(h.created * 1000).toLocaleString(
															undefined,
															{
																month: "short",
																day: "numeric",
																hour: "numeric",
																minute: "2-digit",
															},
														)}
													</span>
													{h.expired && (
														<span className="text-orange-500">· expired</span>
													)}
													{!h.expired && !h.audio && (
														<span className="text-muted-foreground">
															· no audio
														</span>
													)}
													{h.metadata?.response_format && (
														<span className="uppercase">
															· {h.metadata.response_format}
														</span>
													)}
												</div>
											</div>
										</button>
									</li>
								))}
							</ul>
						)}
					</div>
				</div>

				<div className="rounded-xl border border-border/60 bg-muted/30 p-3">
					<div className="mb-1.5 flex items-center gap-1.5 text-xs font-medium">
						<Lightbulb className="h-3.5 w-3.5 text-muted-foreground" />
						Prompt tips
					</div>
					<ul className="space-y-1.5 text-[12px] leading-relaxed text-muted-foreground">
						<li>Name the genre, mood, and instruments.</li>
						<li>
							Add tempo (e.g. <span className="font-mono">90 BPM</span>) and
							structure (intro / verse / chorus).
						</li>
						<li>
							Toggle <span className="font-medium">Instrumental</span> to skip
							vocals.
						</li>
						<li>Pro: attach reference images for visual inspiration.</li>
						<li>Don't name real artists or copyrighted lyrics — blocked.</li>
					</ul>
				</div>
				<div className="rounded-xl border border-border/60 bg-muted/30 p-3">
					<div className="mb-1.5 flex items-center gap-1.5 text-xs font-medium text-foreground">
						<Clock className="h-3.5 w-3.5 text-muted-foreground" />
						Storage &amp; retention
					</div>
					<ul className="space-y-1 text-[11px] leading-relaxed text-muted-foreground">
						<li>
							Generated tracks are stored for{" "}
							<span className="font-semibold text-foreground">30 days</span>,
							then auto-deleted.
						</li>
						<li>
							Set your org's data retention to{" "}
							<span className="font-mono">none</span> to skip storage entirely —
							tracks return inline and aren't saved.
						</li>
					</ul>
				</div>
			</SidebarContent>

			<SidebarFooter className="gap-2 border-t border-border/60 px-3 py-3">
				<CreditsDisplay organization={selectedOrganization} isLoading={false} />
				<div className="flex items-center justify-between px-1">
					<div className="flex min-w-0 flex-1 items-center gap-2.5">
						<Avatar className="h-8 w-8 border border-border/60">
							<AvatarFallback className="bg-muted text-xs">
								{user?.name?.slice(0, 2) || "AU"}
							</AvatarFallback>
						</Avatar>
						<div className="min-w-0 flex-1">
							<div className="truncate text-sm font-medium leading-tight">
								{user?.name}
							</div>
							<div className="truncate text-[11px] leading-tight text-muted-foreground">
								{user?.email}
							</div>
						</div>
					</div>
					<Button
						variant="ghost"
						size="sm"
						onClick={logout}
						className="h-8 w-8 shrink-0 rounded-lg p-0"
						title="Sign out"
						aria-label="Sign out"
					>
						<LogOutIcon className="h-3.5 w-3.5" />
					</Button>
				</div>
			</SidebarFooter>
		</Sidebar>
	);
}
